import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import IdleTimer from './components/IdleTimer';

// Auth
const LoginPage = lazy(() => import('./features/Auth/LoginPage'));
const RegisterPage = lazy(() => import('./features/Auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./features/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./features/Auth/ResetPasswordPage'));

// Public
const InvitePage = lazy(() => import('./features/Public/InvitePage'));

// Admin
const AdminDashboard = lazy(() => import('./features/Admin/DashboardPage'));
const AdminAmbassadors = lazy(() => import('./features/Admin/AmbassadorsPage'));
const AdminAmbassadorDetail = lazy(() => import('./features/Admin/AmbassadorDetailPage'));
const AdminPartners = lazy(() => import('./features/Admin/PartnersPage'));
const AdminPartnerDetail = lazy(() => import('./features/Admin/PartnerDetailPage'));
const AdminLeads = lazy(() => import('./features/Admin/LeadsPage'));
const AdminLeadDetail = lazy(() => import('./features/Admin/LeadDetailPage'));
// const AdminCommissions = lazy(() => import('./features/Admin/CommissionsPage')); // hidden per stakeholder request
const AdminPublications = lazy(() => import('./features/Admin/PublicationsPage'));
const AdminChallenges = lazy(() => import('./features/Admin/ChallengesPage'));
const AdminChallengeDetail = lazy(() => import('./features/Admin/ChallengeDetailPage'));
const AdminEmailTemplates = lazy(() => import('./features/Admin/EmailTemplatesPage'));
const AdminReleases = lazy(() => import('./features/Admin/ReleasesPage'));
const AdminCategories = lazy(() => import('./features/Admin/CategoriesPage'));
const AdminProspectionTemplates = lazy(() => import('./features/Admin/ProspectionTemplatesPage'));
const AdminNotifications = lazy(() => import('./features/Admin/NotificationsPage'));
const AdminUserManagement = lazy(() => import('./features/Admin/UserManagementPage'));
const AdminProfile = lazy(() => import('./features/Admin/ProfilePage'));

// Ambassador
const AmbassadorDashboard = lazy(() => import('./features/Ambassador/DashboardPage'));
const AmbassadorLeadForm = lazy(() => import('./features/Ambassador/LeadFormPage'));
const AmbassadorLeads = lazy(() => import('./features/Ambassador/LeadsPage'));
const AmbassadorLeadDetail = lazy(() => import('./features/Ambassador/LeadDetailPage'));
const AmbassadorPublications = lazy(() => import('./features/Ambassador/PublicationsPage'));
const AmbassadorChallenges = lazy(() => import('./features/Ambassador/ChallengesPage'));
const AmbassadorChallengeDetail = lazy(() => import('./features/Ambassador/ChallengeDetailPage'));
const AmbassadorProspectionKit = lazy(() => import('./features/Ambassador/ProspectionKitPage'));
const AmbassadorProfile = lazy(() => import('./features/Ambassador/ProfilePage'));

// Partner
const PartnerDashboard = lazy(() => import('./features/Partner/DashboardPage'));
const PartnerLeads = lazy(() => import('./features/Partner/LeadsPage'));
const PartnerLeadDetail = lazy(() => import('./features/Partner/LeadDetailPage'));
const PartnerClients = lazy(() => import('./features/Partner/ClientsPage'));
const PartnerClientDetail = lazy(() => import('./features/Partner/ClientDetailPage'));
const PartnerSubAccounts = lazy(() => import('./features/Partner/SubAccountsPage'));
const PartnerProfile = lazy(() => import('./features/Partner/ProfilePage'));

// Messaging (shared)
const MessagesPage = lazy(() => import('./features/Messaging/MessagesPage'));

function LoadingSpinner() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: 'var(--bg-surface)' }}
    >
      <div
        className="animate-spin rounded-full h-10 w-10 border-2 border-transparent"
        style={{ borderTopColor: '#F5D547' }}
      />
    </div>
  );
}

function RoleRedirect() {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'ambassador') return <Navigate to="/ambassador" replace />;
  if (user?.role === 'partner') return <Navigate to="/partner" replace />;

  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      {/* Auto-disconnect on inactivity — self-gates on auth, mounted once. */}
      <IdleTimer />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite/:ambassadorId" element={<InvitePage />} />
        <Route path="/" element={<RoleRedirect />} />

        {/* Admin routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute role="admin">
              <Routes>
                <Route index element={<AdminDashboard />} />
                <Route path="ambassadeurs" element={<AdminAmbassadors />} />
                <Route path="ambassadeurs/:id" element={<AdminAmbassadorDetail />} />
                <Route path="partenaires" element={<AdminPartners />} />
                <Route path="partenaires/:id" element={<AdminPartnerDetail />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="leads/:id" element={<AdminLeadDetail />} />
                {/* <Route path="commissions" element={<AdminCommissions />} /> */}{/* hidden per stakeholder request */}
                <Route path="publications" element={<AdminPublications />} />
                <Route path="challenges" element={<AdminChallenges />} />
                <Route path="challenges/:id" element={<AdminChallengeDetail />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="email-templates" element={<AdminEmailTemplates />} />
                <Route path="releases" element={<AdminReleases />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="prospection-templates" element={<AdminProspectionTemplates />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="utilisateurs" element={<AdminUserManagement />} />
                <Route path="profil" element={<AdminProfile />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Routes>
            </ProtectedRoute>
          }
        />

        {/* Ambassador routes */}
        <Route
          path="/ambassador/*"
          element={
            <ProtectedRoute role="ambassador">
              <Routes>
                <Route index element={<AmbassadorDashboard />} />
                <Route path="leads" element={<AmbassadorLeads />} />
                <Route path="leads/new" element={<AmbassadorLeadForm />} />
                <Route path="leads/:id" element={<AmbassadorLeadDetail />} />
                <Route path="publications" element={<AmbassadorPublications />} />
                <Route path="challenges" element={<AmbassadorChallenges />} />
                <Route path="challenges/:id" element={<AmbassadorChallengeDetail />} />
                <Route path="kit-prospection" element={<AmbassadorProspectionKit />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="profil" element={<AmbassadorProfile />} />
                <Route path="*" element={<Navigate to="/ambassador" replace />} />
              </Routes>
            </ProtectedRoute>
          }
        />

        {/* Partner routes */}
        <Route
          path="/partner/*"
          element={
            <ProtectedRoute role="partner">
              <Routes>
                <Route index element={<PartnerDashboard />} />
                <Route path="leads" element={<PartnerLeads />} />
                <Route path="leads/:id" element={<PartnerLeadDetail />} />
                <Route path="clients" element={<PartnerClients />} />
                <Route path="clients/:id" element={<PartnerClientDetail />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="sub-accounts" element={<PartnerSubAccounts />} />
                <Route path="profil" element={<PartnerProfile />} />
                <Route path="*" element={<Navigate to="/partner" replace />} />
              </Routes>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}
